/**
 * Structured Logger with Correlation IDs
 * 
 * Cloud Logging compatible structured logging with:
 * - Correlation IDs for request tracing
 * - JSON format option for log aggregation
 * - PII redaction utilities
 * - Severity levels matching Cloud Logging
 */

import { ENVIRONMENT, isProduction } from "@/config/environment";

// Cloud Logging severity levels
export type Severity = "DEBUG" | "INFO" | "NOTICE" | "WARNING" | "ERROR" | "CRITICAL";

// Structured log entry format (Cloud Logging compatible)
export interface StructuredLogEntry {
  severity: Severity;
  message: string;
  timestamp: string;
  correlationId?: string;
  traceId?: string;
  spanId?: string;
  labels?: Record<string, string>;
  context?: Record<string, unknown>;
  errorId?: string;
  // Cloud Logging specific fields
  "logging.googleapis.com/labels"?: Record<string, string>;
}

// Active correlation ID for request context
let activeCorrelationId: string | null = null;

/**
 * Generate a unique correlation ID
 */
export function generateCorrelationId(): string {
  return `cor_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Generate a unique trace ID (for distributed tracing)
 */
export function generateTraceId(): string {
  return `trace_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Set the active correlation ID for the current request context
 */
export function setCorrelationId(id: string): void {
  activeCorrelationId = id;
}

/**
 * Get the current correlation ID
 */
export function getCorrelationId(): string | null {
  return activeCorrelationId;
}

/**
 * Clear the correlation ID (call after request completes)
 */
export function clearCorrelationId(): void {
  activeCorrelationId = null;
}

/**
 * Redact PII from log data
 */
export function redactPII(data: Record<string, unknown>): Record<string, unknown> {
  const piiFields = ["email", "password", "phone", "ssn", "credit_card", "address", "dob", "date_of_birth"];
  const redacted = { ...data };
  
  for (const key of Object.keys(redacted)) {
    const lowerKey = key.toLowerCase();
    if (piiFields.some(pii => lowerKey.includes(pii))) {
      redacted[key] = "[REDACTED]";
    } else if (typeof redacted[key] === "object" && redacted[key] !== null) {
      redacted[key] = redactPII(redacted[key] as Record<string, unknown>);
    }
  }
  
  return redacted;
}

/**
 * Format log entry as JSON for Cloud Logging
 */
function formatAsJSON(entry: StructuredLogEntry): string {
  return JSON.stringify({
    ...entry,
    "logging.googleapis.com/labels": entry.labels,
  });
}

/**
 * Format log entry for console output
 */
function formatForConsole(entry: StructuredLogEntry): string {
  const correlationPart = entry.correlationId ? ` [${entry.correlationId}]` : "";
  return `[${entry.timestamp}] [${entry.severity}]${correlationPart} ${entry.message}`;
}

// In-memory log buffer for debugging
const logBuffer: StructuredLogEntry[] = [];
const MAX_BUFFER_SIZE = 100;

/**
 * Core structured logging function
 */
function logStructured(
  severity: Severity,
  message: string,
  context?: Record<string, unknown>,
  options?: { skipPIIRedaction?: boolean }
): StructuredLogEntry {
  const entry: StructuredLogEntry = {
    severity,
    message,
    timestamp: new Date().toISOString(),
    correlationId: activeCorrelationId || undefined,
    context: options?.skipPIIRedaction ? context : context ? redactPII(context) : undefined,
    labels: {
      environment: ENVIRONMENT,
      component: "frontend",
    },
  };

  if (severity === "ERROR" || severity === "CRITICAL") {
    entry.errorId = `err_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  // Add to buffer
  logBuffer.push(entry);
  if (logBuffer.length > MAX_BUFFER_SIZE) {
    logBuffer.shift();
  }

  // Output based on environment and format preference
  const useJSON = isProduction || import.meta.env.VITE_LOG_FORMAT === "json";
  
  if (useJSON) {
    console.log(formatAsJSON(entry));
  } else {
    const consoleMethod = severity === "ERROR" || severity === "CRITICAL" 
      ? console.error 
      : severity === "WARNING" 
        ? console.warn 
        : console.log;
    
    consoleMethod(formatForConsole(entry), context || "");
  }

  return entry;
}

/**
 * Structured logger API
 */
export const structuredLogger = {
  debug: (message: string, context?: Record<string, unknown>) => 
    logStructured("DEBUG", message, context),
  
  info: (message: string, context?: Record<string, unknown>) => 
    logStructured("INFO", message, context),
  
  notice: (message: string, context?: Record<string, unknown>) => 
    logStructured("NOTICE", message, context),
  
  warn: (message: string, context?: Record<string, unknown>) => 
    logStructured("WARNING", message, context),
  
  error: (message: string, context?: Record<string, unknown>) => 
    logStructured("ERROR", message, context),
  
  critical: (message: string, context?: Record<string, unknown>) => 
    logStructured("CRITICAL", message, context),

  /**
   * Log an error with stack trace
   */
  captureError: (error: Error, context?: Record<string, unknown>) => {
    return logStructured("ERROR", error.message, {
      ...context,
      stack: error.stack,
      name: error.name,
    });
  },

  /**
   * Get recent log entries for debugging
   */
  getRecentLogs: (count = 20): StructuredLogEntry[] => {
    return logBuffer.slice(-count);
  },

  /**
   * Export logs as JSON array (for debugging/export)
   */
  exportLogs: (): string => {
    return JSON.stringify(logBuffer, null, 2);
  },

  /**
   * Clear log buffer
   */
  clearLogs: (): void => {
    logBuffer.length = 0;
  },
};

/**
 * Request context wrapper for automatic correlation ID management
 */
export function withCorrelationId<T>(
  fn: () => T | Promise<T>,
  correlationId?: string
): T | Promise<T> {
  const id = correlationId || generateCorrelationId();
  setCorrelationId(id);
  
  try {
    const result = fn();
    if (result instanceof Promise) {
      return result.finally(() => clearCorrelationId());
    }
    clearCorrelationId();
    return result;
  } catch (error) {
    clearCorrelationId();
    throw error;
  }
}
